import GeoJSON from 'ol/format/GeoJSON';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import JSZip from 'jszip';
import type { AttributeValue, OriginalAttributes, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { createGenericLayer } from '../domain/layers';
import { selectPrimaryKml } from './importService';
import { parseKmlFeatures } from './kmlParser';

const extension = (name: string): string => name.split('.').pop()?.toLowerCase() ?? '';
const normalizeValue = (value: unknown): AttributeValue => value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : value === undefined ? null : JSON.stringify(value);
const stableSlug = (value: string): string => value.normalize('NFKD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'capa';
let importSequence = 0;

const recordsFromFeatures = (features: readonly Feature<Geometry>[], layerId: string): VectorFeatureRecord[] => features.flatMap((feature, index) => {
  if (!feature.getGeometry()) return [];
  const attributes: Record<string, AttributeValue> = {};
  Object.entries(feature.getProperties()).forEach(([key, value]) => {
    if (key !== 'geometry' && key !== 'Style') attributes[key] = normalizeValue(value);
  });
  const sourceId = feature.getId();
  const id = `${layerId}/feature:${sourceId === undefined ? 'sin-id' : String(sourceId)}:${index}`;
  feature.set('layerId', layerId, true);
  feature.set('genericFeatureId', id, true);
  return [{ id, feature, attributes: attributes satisfies OriginalAttributes }];
});

const createId = (name: string): string => `layer:generic:${stableSlug(name)}:${++importSequence}`;

export function parseGeoJsonLayer(text: string, sourceName: string, id = createId(sourceName)): VectorLayerModel {
  let value: unknown;
  try { value = JSON.parse(text); } catch { throw new Error('El archivo JSON no contiene JSON válido.'); }
  if (!value || typeof value !== 'object' || !('type' in value) || !['FeatureCollection', 'Feature', 'GeometryCollection'].includes(String((value as { type?: unknown }).type))) {
    throw new Error('El archivo JSON no es GeoJSON válido.');
  }
  const crsName = String((value as { crs?: { properties?: { name?: unknown } } }).crs?.properties?.name ?? '');
  const declaredCrs = /3857|900913/i.test(crsName) ? 'EPSG:3857' : /4326|CRS84/i.test(crsName) ? 'EPSG:4326' : null;
  const sourceCrs = declaredCrs ?? 'EPSG:4326';
  const features = new GeoJSON().readFeatures(value, { dataProjection: sourceCrs, featureProjection: 'EPSG:3857' }) as Feature<Geometry>[];
  return createGenericLayer({ id, name: sourceName.replace(/\.[^.]+$/, ''), sourceName, sourceFormat: 'GeoJSON', sourceCrs, assumedCrs: !declaredCrs, records: recordsFromFeatures(features, id) });
}

export function parseGenericKmlLayer(text: string, sourceName: string, sourceFormat: 'KML' | 'KMZ', id = createId(sourceName)): VectorLayerModel {
  const parsed = parseKmlFeatures(text, { includeRawDescription: true });
  const records: VectorFeatureRecord[] = parsed.map(({ feature, attributes }, index) => {
    const recordId = `${id}/feature:${index}`;
    feature.set('layerId', id, true);
    feature.set('genericFeatureId', recordId, true);
    return { id: recordId, feature, attributes };
  });
  return createGenericLayer({ id, name: sourceName.replace(/\.[^.]+$/, ''), sourceName, sourceFormat, sourceCrs: 'EPSG:4326', assumedCrs: false, records });
}

export async function importVectorLayer(file: File): Promise<VectorLayerModel> {
  const ext = extension(file.name);
  if (ext === 'geojson' || ext === 'json') return parseGeoJsonLayer(await file.text(), file.name);
  if (ext === 'kml') return parseGenericKmlLayer(await file.text(), file.name, 'KML');
  if (ext === 'kmz') {
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const entry = selectPrimaryKml(Object.values(zip.files));
    if (!entry) throw new Error('El KMZ no contiene un archivo KML.');
    return parseGenericKmlLayer(await entry.async('string'), file.name, 'KMZ');
  }
  throw new Error('Importar datos admite GeoJSON, KML y KMZ para este flujo.');
}
